<script setup>
import { defineProps, defineEmits } from 'vue'
import ObstacleShape from './ObstacleShape.vue'
import { formatDecimalBR } from '@/components/Ajuda/Montador/lib/numbers'

const props = defineProps({
  obstacles:   { type: Array,  required: true },
  selectedId:  { type: [Number, String, null], default: null },
  scale:       { type: Number, required: true },
  viewScale:   { type: Number, default: 1 },
  getObsSpecs: { type: Function, required: true },
  courseLabel: { type: Function, required: true },
})
const emit = defineEmits(['obstacle-down', 'obstacle-click', 'rotate-down'])

function obstacleStyle(obs, getObsSpecs, scale) {
  const spec = getObsSpecs(obs)
  const w = spec.wm * scale
  const h = spec.hm * scale
  const x = obs.xm * scale
  const y = obs.ym * scale

  const style = {
    width: w + 'px',
    height: h + 'px',
    transform: `translate(${x - w/2}px, ${y - h/2}px) rotate(${obs.rot}deg)`,
    zIndex: obs.z,
  }

  return style
}

/* ✅ usar props.getObsSpecs aqui */
function dimsInfo(obs) {
  const s = props.getObsSpecs(obs)
  const type = String(obs?.type || '').toLowerCase()

  // 1) largura sempre
  const w = `${formatDecimalBR(s.wm, 2)} m`

  // 2) alturas: preferir barras; fallback hm só se não houver
  const H = Array.isArray(s.heights)
    ? s.heights.map(Number).filter(Number.isFinite)
    : []

  let hText = ''
  if (type === 'start' || type === 'finish') {
    hText = '' // não mostra altura em Start/Finish
  } else if (type === 'oxer') {
    hText = H.length
      ? H.slice(0, 2).map(v => `${formatDecimalBR(v, 2)} m`).join(' / ')
      : (s.hm != null ? `${formatDecimalBR(s.hm, 2)} m` : '')
  } else if (type === 'triplebar' || type === 'triple') {
    hText = H.length
      ? H.slice(0, 3).map(v => `${formatDecimalBR(v, 2)} m`).join(' / ')
      : (s.hm != null ? `${formatDecimalBR(s.hm, 2)} m` : '')
  } else {
    const h0 = H.length ? H[0] : s.hm
    hText = (h0 != null) ? `${formatDecimalBR(h0, 2)} m` : ''
  }

  return { w, hText }
}
</script>

<template>
  <div
    v-for="(obs, idx) in obstacles"
    :key="obs?.id ?? `obs-${idx}`"
    class="obs"
    :class="{ selected: obs?.id === selectedId }"
    :data-type="obs?.type"
    :style="obstacleStyle(obs, getObsSpecs, scale)"
    @pointerdown.stop="emit('obstacle-down', $event, obs)"
    @click.stop="emit('obstacle-click', obs, $event)"
  >
    <ObstacleShape
      :obs="obs"
      :px="scale"
      :spec="getObsSpecs(obs)"
      :viewScale="viewScale"
      styleVariant="classic"
    />

    <div class="label" v-if="courseLabel(obs)"
         :style="{ transform: `rotate(${-obs.rot}deg)` }">
      {{ courseLabel(obs) }}
    </div>

    <div class="dims" v-if="obs?.id === selectedId">
      <div class="dim-line dim-w">{{ dimsInfo(obs).w }}</div>
      <div class="dim-line dim-h" v-if="dimsInfo(obs).hText">{{ dimsInfo(obs).hText }}</div>
    </div>

    <div v-if="obs?.id === selectedId"
         class="rot-handle"
         @pointerdown.stop="emit('rotate-down', $event, obs)"></div>
  </div>
</template>

<style scoped>
.obs{
  position:absolute; transform-origin:50% 50%;
  cursor:grab; overflow:visible;
}
.obs.selected{
  outline:2px dashed #2563eb; outline-offset:2px;
  box-shadow:0 0 0 4px rgba(37,99,235,.15) inset;
}
.obs .label{
  position:absolute; right:6px; top:6px;
  font-weight:800; font-size:12px; color:#111;
  background:rgba(255,255,255,.7); padding:2px 6px; border-radius:6px;
}
.obs .dims{
  position:absolute; left:6px; bottom:6px;
  font-size:12px; font-weight:600; color:#0f172a;
  background:rgba(255,255,255,.5); padding:2px 6px; border-radius:6px;
}
.obs .rot-handle{
  position:absolute; left:50%; top:-22px; transform:translateX(-50%);
  width:14px; height:14px; border-radius:50%;
  background:#2563eb; border:2px solid #eaeef3; cursor:grab;
}
.jump.map{ display:block; }
.bar-rect, .bar-seg{ shape-rendering:crispEdges; }

.dims{
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  background: rgba(255,255,255,.5);
  color: #0f172a;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  /* evita que algum overflow/line-height corte a 1ª linha */
  line-height: 1.1;
}
.dim-line { line-height: 1.1; }
.dim-w { font-weight: 700; }           /* linha de largura */
.dim-h { font-weight: 600; opacity:.95 } /* linha de alturas */

/* Esconde número/legenda e dimensões */
.obs .dims {
  display: none !important;
}

</style>
